import { CompanyFilter } from "../../legacy/types/companyFilter";
import { Workspace, Market } from "../../types/workspace";

import { isEmpty, get } from "../jsUtils";
import { transformSamGeography } from "./transformers/geographyTransformer";
import { transformSamIndustry } from "./transformers/industryTransformer";
import { createProductFilter } from "./transformers/productTransformer";

/**
 * Transforms workspace configuration (SAM and SOM, excluding TAM) into CompanyFilter format
 * and merges it with provided userFilters.
 *
 * User filters take precedence over workspace configuration when there's overlap.
 * If a specific user filter is empty or null, the workspace configuration will be used.
 *
 * @param selectedMarket The selected market
 * @param workspace The workspace containing configuration to transform
 * @param userFilters Optional user filters to merge with
 * @returns A CompanyFilter object combining workspace configuration with user filters
 */
export const transformWorkspaceToCompanyFilter = (
  selectedMarket: Market,
  workspace: Workspace | undefined,
  userFilters?: CompanyFilter
): CompanyFilter | undefined => {
  if (!workspace) return undefined;

  // Initial result will be from workspace configuration
  let result: CompanyFilter = {};

  // Transform SAM configuration to CompanyFilter format
  const sam = get(workspace, "configuration.sam");
  if (sam && (selectedMarket === Market.SAM || selectedMarket === Market.SOM)) {
    const { industry, geography } = sam;

    // Transform industry data if user hasn't specified industry filters
    if (industry && isEmpty(get(userFilters, "industry"))) {
      result.industry = transformSamIndustry(industry, undefined);
    }

    // Transform geography data if user hasn't specified corresponding filters
    if (geography) {
      const workspaceGeographyValues = [
        get(geography, "stateIds", []),
        get(geography, "regionsIds", []),
        get(geography, "countryIds", []),
        get(geography, "excludedCountryIds", []),
      ].flat();

      const isUserGeographyValuesEmpty = [
        isEmpty(get(userFilters, "stateIds")),
        isEmpty(get(userFilters, "regionNames")),
        isEmpty(get(userFilters, "countryAlpha2s")),
        isEmpty(get(userFilters, "excludedCountryAlpha2s")),
      ].every((value) => !!value);

      if (workspaceGeographyValues.length > 0 && isUserGeographyValuesEmpty) {
        result = transformSamGeography(geography, result);
      }
    }
  }

  // Transform SOM configuration to CompanyFilter format
  const som = get(workspace, "configuration.som");
  if (som && selectedMarket === Market.SOM) {
    const {
      employeeRange,
      revenueRange,
      productIds,
      excludedProductIds,
      productsInclusionMethod,
      excludedProductsInclusionMethod,
    } = som;

    // Add employee range filter if valid values exist and user hasn't set employees filter
    const userEmployees = get(userFilters, "employees", {});

    if (employeeRange && isEmpty(userEmployees)) {
      result.employees = {
        min: employeeRange.min > 0 ? employeeRange.min : undefined,
        max: employeeRange.max > 0 ? employeeRange.max : undefined,
      };
    }

    // Add revenue range filter if valid values exist and user hasn't set revenue filter
    const userRevenue = get(userFilters, "revenue", {});
    if (revenueRange && isEmpty(userRevenue)) {
      result.revenue = {
        min: revenueRange.min > 0 ? revenueRange.min : undefined,
        max: revenueRange.max > 0 ? revenueRange.max : undefined,
      };
    }

    // Handle product filters if user hasn't specified any
    const userProducts = get(userFilters, "products", []);
    if (isEmpty(userProducts)) {
      const productFilters = [];

      // Add included products
      if (productIds?.length) {
        productFilters.push(
          createProductFilter(
            productIds,
            productsInclusionMethod || "ANY_PRESENT"
          )
        );
      }

      // Add excluded products
      if (excludedProductIds?.length) {
        productFilters.push(
          createProductFilter(
            excludedProductIds,
            excludedProductsInclusionMethod || "NONE_PRESENT"
          )
        );
      }

      // Only add products array if there are any product filters
      if (productFilters.length > 0) {
        result.products = productFilters;
      }
    }
  }

  if (!userFilters) return result;

  const definedUserFilters = Object.fromEntries(
    Object.entries(userFilters).filter(([_, value]) => value !== undefined)
  );

  // Merge with filtered userFilters, giving them priority
  return { ...result, ...definedUserFilters };
};
